import type { PageId } from '../../routes.ts';

/**
 * `footer` — bản tiếng Anh, lấy từ thuộc tính `data-en` trong `template.html` (1054–1066).
 * Hình dạng phải khớp `vi/footer.ts`; lệch một khoá là lỗi biên dịch.
 *
 * `href` GIỮ NGUYÊN ở mọi ngôn ngữ (anchor trong trang), trừ `newera.inc` vốn có bản `/vi` và
 * `/en` riêng — đó là lý do `href` nằm trong copy chứ không phải hằng như hai nút `#cta`.
 */
/**
 * UNION có chủ ý, hai nhánh KHÔNG thay nhau được:
 *   · `href` — anchor trong trang (`#security`) hoặc link RA NGOÀI (`https://newera.inc/vi`).
 *   · `page` — trang NỘI BỘ, khai bằng id route. Đường dẫn do `pathFor()` sinh lúc render.
 *
 * Vì sao trang nội bộ KHÔNG được dùng `href`: gõ `/vi/privacy/` bằng tay là (1) dựng nguồn sự thật
 * thứ hai về slug bên cạnh bảng route, và (2) mở cửa cho link CHÉO ngôn ngữ — footer render trên
 * MỌI trang, nên một href cứng `/vi/…` sẽ kéo người đọc bản `en` sang bản `vi` và làm Google gộp
 * hai bản. `PageLink` lấy locale từ context nên không thể quên prefix. Đừng lách bằng `href`.
 */
type FooterLink =
  | { readonly href: string; readonly label: string }
  | { readonly page: PageId; readonly label: string };

interface FooterColumn {
  readonly label: string;
  readonly links: readonly FooterLink[];
}

export const footer = {
  tagline: 'Nexa is a product of NewEra Inc. — trust and technology, reimagined.',
  logoAlt: 'NewEra Inc.',
  columns: [
    {
      label: 'Product',
      links: [
        { href: '#modules', label: 'Modules' },
        { href: '#collab', label: 'Chat & Topic' },
        { href: '#pm', label: 'Project Manager' },
        { href: '#ai', label: 'AI Agents' },
      ],
    },
    {
      label: 'Company',
      links: [
        { href: 'https://newera.inc/en', label: 'newera.inc' },
        { href: '#security', label: 'Security' },
        { href: '#pricing', label: 'Pricing' },
        { href: '#faq', label: 'FAQ' },
      ],
    },
    /*
     * Cột PHÁP LÝ — thêm ở lô này, và nó KHÔNG có trong bản thiết kế (`template.html:1054` chỉ có
     * 3 cột: Sản phẩm · Công ty · Liên hệ). Đây là ngoại lệ user chốt 2026-08-27, không phải chép
     * sai thiết kế: 5 trang này đã tồn tại và đã prerender từ lâu, nhưng footer không trỏ tới trang
     * nào cả — tức chúng sống mà không ai tới được, và không cổng nào đỏ vì mỗi trang tự nó vẫn
     * đúng. Một site công khai phải có đường đi tới chính sách, điều khoản, cookie, kênh hỗ trợ và
     * quyền xoá tài khoản; thiếu là rớt app review và trái GDPR.
     *
     * Tập trang lấy theo nhánh `v1` (5 link, đúng thứ tự này) — `v1:src/lib/components/Footer.svelte`.
     * KHÔNG có `terms/v1` và `privacy/v1`: chúng là văn bản đã hết hiệu lực, gắn `noindex`, và đường
     * tới chúng là `VersionSwitcher` NẰM TRONG trang hiện hành. Đưa lên footer là mời người đọc vào
     * bản cũ như thể nó đang áp dụng. Cổng canh cả hai chiều: `copy.test.ts` ràng tập này khớp đúng
     * `LEGAL_PAGES` lọc theo `indexable`, nên thêm trang pháp lý mới mà quên footer là ĐỎ.
     *
     * LƯU Ý — NHÃN Ở ĐÂY LÀ NHÃN ĐIỀU HƯỚNG, KHÔNG PHẢI TIÊU ĐỀ VĂN BẢN — đừng "dọn" về
     * `legalDocMeta(id, locale).title`. Đã đo: tiêu đề thật là `Yêu cầu xóa tài khoản Nexa` (26 ký
     * tự) và `Request Nexa account deletion` (29). Cột footer rộng ~180px ở 1440 nên hai chuỗi đó
     * vỡ 3 dòng và phá lưới. Tiêu đề văn bản vẫn là h1 của trang và vẫn TRA từ docMeta ở
     * `copy/<locale>/legal.ts` — chỗ đó không đổi. Hai chuỗi khác nhau vì hai VAI khác nhau.
     */
    {
      label: 'Legal',
      links: [
        { page: 'legal-privacy', label: 'Privacy' },
        { page: 'legal-terms', label: 'Terms' },
        { page: 'legal-cookies', label: 'Cookies' },
        { page: 'legal-support', label: 'Support' },
        { page: 'legal-delete-account', label: 'Delete account' },
      ],
    },
    {
      label: 'Apps',
      links: [
        { href: '#download', label: 'macOS' },
        { href: '#download', label: 'Windows' },
      ],
    },
  ] as readonly FooterColumn[],
  contact: {
    label: 'Contact',
    items: ['Book a demo', 'hello@newera.inc', 'Ho Chi Minh City, Vietnam'] as readonly string[],
  },
  rights: 'All rights reserved.',
};
